/**
 * Generate a unique ID
 * @returns {string} A unique ID
 */
export const generateId = () => {
  return Date.now().toString(36) + Math.random().toString(36).substr(2, 5);
};

/**
 * Generate a match schedule based on teams and tournament settings
 * @param {Array} teams - Array of team objects
 * @param {Object} tournament - Tournament settings object
 * @returns {Array} Array of match objects
 */
export const generateSchedule = (teams, tournament) => {
  const { format, eventDuration, matchDuration, breakDuration, fields, hasFinals } = tournament;
  const matches = [];
  let gameNumber = 1; // Counter for game numbers
  
  if (teams.length < 2) return matches;
  
  // Calculate how many matches we can fit in the event
  const totalMinutes = eventDuration;
  const matchPlusBreaKDuration = matchDuration + breakDuration;
  const maxMatches = Math.floor(totalMinutes / matchPlusBreaKDuration);
  
  // Calculate how many full round robin rounds we can fit
  const matchesPerRound = (teams.length * (teams.length - 1)) / 2;
  const possibleRounds = Math.floor(maxMatches / matchesPerRound);
  const numRounds = Math.max(1, possibleRounds); // At least 1 round
  
  console.log(`Can fit ${numRounds} round robin rounds`);
  
  // Generate multiple rounds of round robin if time permits
  for (let round = 0; round < numRounds; round++) {
    if (teams.length <= 4) {
      // Simple round-robin tournament for small number of teams
      generateRoundRobinSchedule(teams, tournament, matches, gameNumber, round);
      gameNumber += matchesPerRound; // Update game number counter
    } else if (hasFinals && round === numRounds - 1) {
      // Last round is finals if requested
      if (isPowerOfTwo(teams.length)) {
        generateKnockoutSchedule(teams, tournament, matches, gameNumber);
      } else {
        generateGroupAndKnockoutSchedule(teams, tournament, matches, gameNumber);
      }
    } else {
      // Regular round-robin for middle rounds
      generateRoundRobinSchedule(teams, tournament, matches, gameNumber, round);
      gameNumber += matchesPerRound; // Update game number counter
    }
  }
  
  return matches;
};

/**
 * Check if a number is a power of 2
 * @param {number} num - Number to check
 * @returns {boolean} True if number is power of 2
 */
const isPowerOfTwo = (num) => {
  return num > 0 && (num & (num - 1)) === 0;
};

/**
 * Generate a round-robin tournament schedule
 * @param {Array} teams - Array of team objects
 * @param {Object} tournament - Tournament settings object
 * @param {Array} matches - Array to populate with match objects
 * @param {number} startGameNumber - Starting game number
 * @param {number} roundNumber - Current round number (0-based)
 */
const generateRoundRobinSchedule = (teams, tournament, matches, startGameNumber = 1) => {
  const { matchDuration, breakDuration, fields } = tournament;
  let matchTime = matches.length > 0 
    ? matches[matches.length - 1].startTime + matchDuration + breakDuration 
    : 0;
  let gameNumber = startGameNumber;
  
  // Iterate over all pairs of teams
  for (let i = 0; i < teams.length; i++) {
    for (let j = i + 1; j < teams.length; j++) {
      const homeTeam = teams[i];
      const awayTeam = teams[j];
      
      // Select field (cycle through available fields)
      const fieldIndex = (gameNumber - 1) % fields.length;
      const field = fields[fieldIndex];
      
      // Create match
      matches.push({
        id: generateId(),
        homeTeam: homeTeam,
        awayTeam: awayTeam,
        homeScore: 0,
        awayScore: 0,
        events: [],
        startTime: matchTime,
        duration: matchDuration,
        status: 'scheduled',
        round: 'all-play-all', // Simplified round name
        fieldId: field.id,
        fieldName: field.name,
        gameNumber: gameNumber
      });
      
      // Increment game number
      gameNumber++;
      
      // Increment match time for next match
      matchTime += (matchDuration + breakDuration);
    }
  }
};

/**
 * Generate a knockout tournament schedule
 * @param {Array} teams - Array of team objects
 * @param {Object} tournament - Tournament settings object
 * @param {Array} matches - Array to populate with match objects
 * @param {number} startGameNumber - Starting game number
 */
const generateKnockoutSchedule = (teams, tournament, matches, startGameNumber = 1) => {
  const { matchDuration, breakDuration, fields } = tournament;
  let matchTime = matches.length > 0 
    ? matches[matches.length - 1].startTime + matchDuration + breakDuration 
    : 0;
  let gameNumber = startGameNumber;
  
  // Shuffle teams for random matchups
  const shuffledTeams = [...teams].sort(() => Math.random() - 0.5);
  
  // First round matches
  for (let i = 0; i < shuffledTeams.length; i += 2) {
    // Select field (cycle through available fields)
    const fieldIndex = (gameNumber - 1) % fields.length;
    const field = fields[fieldIndex];

    matches.push({
      id: generateId(),
      homeTeam: shuffledTeams[i],
      awayTeam: shuffledTeams[i + 1],
      homeScore: 0,
      awayScore: 0,
      events: [],
      startTime: matchTime,
      duration: matchDuration,
      status: 'scheduled',
      round: 'final-round-1',
      nextMatchId: null, // Will be filled later
      fieldId: field.id,
      fieldName: field.name,
      gameNumber: gameNumber
    });
    
    // Increment game number
    gameNumber++;
    
    matchTime += (matchDuration + breakDuration);
  }
  
  // Create placeholder matches for later rounds
  let roundSize = shuffledTeams.length / 4;
  let roundNum = 2;
  let previousRoundMatches = matches.filter(m => m.round === `final-round-${roundNum - 1}`);
  
  while (roundSize >= 1) {
    for (let i = 0; i < roundSize; i++) {
      // Select field (cycle through available fields)
      const fieldIndex = (gameNumber - 1) % fields.length;
      const field = fields[fieldIndex];

      const match = {
        id: generateId(),
        homeTeam: null, // To be determined
        awayTeam: null, // To be determined
        homeScore: 0,
        awayScore: 0,
        events: [],
        startTime: matchTime,
        duration: matchDuration,
        status: 'scheduled',
        round: `final-round-${roundNum}`,
        nextMatchId: null,
        fieldId: field.id,
        fieldName: field.name,
        gameNumber: gameNumber
      };
      
      matches.push(match);
      gameNumber++;
      matchTime += (matchDuration + breakDuration);
      
      // Update next match IDs for previous round
      const firstPrevMatch = previousRoundMatches[i * 2];
      const secondPrevMatch = previousRoundMatches[i * 2 + 1];
      
      if (firstPrevMatch) firstPrevMatch.nextMatchId = match.id;
      if (secondPrevMatch) secondPrevMatch.nextMatchId = match.id;
    }
    
    roundSize = roundSize / 2;
    roundNum++;
    previousRoundMatches = matches.filter(m => m.round === `final-round-${roundNum - 1}`);
  }
};

/**
 * Generate a group stage followed by knockout tournament schedule
 * @param {Array} teams - Array of team objects
 * @param {Object} tournament - Tournament settings object
 * @param {Array} matches - Array to populate with match objects
 * @param {number} startGameNumber - Starting game number
 */
const generateGroupAndKnockoutSchedule = (teams, tournament, matches, startGameNumber = 1) => {
  const { matchDuration, breakDuration, fields, hasFinals } = tournament;
  let matchTime = matches.length > 0 
    ? matches[matches.length - 1].startTime + matchDuration + breakDuration 
    : 0;
  let gameNumber = startGameNumber;
  
  // Determine number of groups (aim for 4 teams per group)
  const numGroups = Math.ceil(teams.length / 4);
  const groups = Array.from({ length: numGroups }, () => []);
  
  // Assign teams to groups
  teams.forEach((team, index) => {
    const groupIndex = index % numGroups;
    groups[groupIndex].push(team);
  });
  
  // Generate group stage matches
  groups.forEach((groupTeams, groupIndex) => {
    for (let i = 0; i < groupTeams.length; i++) {
      for (let j = i + 1; j < groupTeams.length; j++) {
        // Select field (cycle through available fields)
        const fieldIndex = (gameNumber - 1) % fields.length;
        const field = fields[fieldIndex];

        matches.push({
          id: generateId(),
          homeTeam: groupTeams[i],
          awayTeam: groupTeams[j],
          homeScore: 0,
          awayScore: 0,
          events: [],
          startTime: matchTime,
          duration: matchDuration,
          status: 'scheduled',
          round: `group-${groupIndex + 1}`,
          fieldId: field.id,
          fieldName: field.name,
          gameNumber: gameNumber
        });
        
        gameNumber++;
        matchTime += (matchDuration + breakDuration);
      }
    }
  });
  
  // Skip finals if not wanted
  if (!hasFinals) return;

  // Plan for knockout stage (will be filled with actual teams later)
  // Assume top 2 teams from each group advance
  const numKnockoutTeams = numGroups * 2;
  
  if (numKnockoutTeams >= 2) {
    const knockoutTeams = Array(numKnockoutTeams).fill(null);
    
    // First round of knockout
    for (let i = 0; i < knockoutTeams.length; i += 2) {
      // Select field (cycle through available fields)
      const fieldIndex = (gameNumber - 1) % fields.length;
      const field = fields[fieldIndex];

      matches.push({
        id: generateId(),
        homeTeam: null, // Will be determined after group stage
        awayTeam: null, // Will be determined after group stage
        homeScore: 0,
        awayScore: 0,
        events: [],
        startTime: matchTime,
        duration: matchDuration,
        status: 'scheduled',
        round: 'knockout-1',
        nextMatchId: null,
        fieldId: field.id,
        fieldName: field.name,
        gameNumber: gameNumber
      });
      
      gameNumber++;
      matchTime += (matchDuration + breakDuration);
    }
    
    // Create subsequent knockout rounds if needed
    let roundSize = knockoutTeams.length / 4;
    let roundNum = 2;
    let previousRoundMatches = matches.filter(m => m.round === `knockout-${roundNum - 1}`);
    
    while (roundSize >= 1) {
      for (let i = 0; i < roundSize; i++) {
        // Select field (cycle through available fields)
        const fieldIndex = (gameNumber - 1) % fields.length;
        const field = fields[fieldIndex];

        const match = {
          id: generateId(),
          homeTeam: null,
          awayTeam: null,
          homeScore: 0,
          awayScore: 0,
          events: [],
          startTime: matchTime,
          duration: matchDuration,
          status: 'scheduled',
          round: `knockout-${roundNum}`,
          nextMatchId: null,
          fieldId: field.id,
          fieldName: field.name,
          gameNumber: gameNumber
        };
        
        matches.push(match);
        gameNumber++;
        matchTime += (matchDuration + breakDuration);
        
        // Update next match IDs for previous round
        const firstPrevMatch = previousRoundMatches[i * 2];
        const secondPrevMatch = previousRoundMatches[i * 2 + 1];
        
        if (firstPrevMatch) firstPrevMatch.nextMatchId = match.id;
        if (secondPrevMatch) secondPrevMatch.nextMatchId = match.id;
      }
      
      roundSize = roundSize / 2;
      roundNum++;
      previousRoundMatches = matches.filter(m => m.round === `knockout-${roundNum - 1}`);
    }
  }
};

/**
 * Calculate team standings for a tournament
 * @param {Array} teams - Array of team objects
 * @param {Array} matches - Array of match objects
 * @returns {Array} Array of team standings objects sorted by points
 */
export const calculateStandings = (teams, matches) => {
  // Initialize standings with zeros
  const standings = teams.map(team => ({
    id: team.id,
    name: team.name,
    logo: team.logo,
    color: team.color,
    played: 0,
    won: 0,
    drawn: 0,
    lost: 0,
    goalsFor: 0,
    goalsAgainst: 0,
    goalDifference: 0,
    points: 0
  }));
  
  // Only consider completed matches
  const completedMatches = matches.filter(match => match.status === 'completed');
  
  // Process each match
  completedMatches.forEach(match => {
    const homeTeamIndex = standings.findIndex(team => team.id === match.homeTeam.id);
    const awayTeamIndex = standings.findIndex(team => team.id === match.awayTeam.id);
    
    if (homeTeamIndex === -1 || awayTeamIndex === -1) return;
    
    // Update matches played
    standings[homeTeamIndex].played += 1;
    standings[awayTeamIndex].played += 1;
    
    // Update goals
    standings[homeTeamIndex].goalsFor += match.homeScore;
    standings[homeTeamIndex].goalsAgainst += match.awayScore;
    standings[awayTeamIndex].goalsFor += match.awayScore;
    standings[awayTeamIndex].goalsAgainst += match.homeScore;
    
    // Calculate goal difference
    standings[homeTeamIndex].goalDifference = 
      standings[homeTeamIndex].goalsFor - standings[homeTeamIndex].goalsAgainst;
    standings[awayTeamIndex].goalDifference = 
      standings[awayTeamIndex].goalsFor - standings[awayTeamIndex].goalsAgainst;
    
    // Update win/draw/loss and points
    if (match.homeScore > match.awayScore) {
      // Home team won
      standings[homeTeamIndex].won += 1;
      standings[homeTeamIndex].points += 3;
      standings[awayTeamIndex].lost += 1;
    } else if (match.homeScore < match.awayScore) {
      // Away team won
      standings[awayTeamIndex].won += 1;
      standings[awayTeamIndex].points += 3;
      standings[homeTeamIndex].lost += 1;
    } else {
      // Draw
      standings[homeTeamIndex].drawn += 1;
      standings[homeTeamIndex].points += 1;
      standings[awayTeamIndex].drawn += 1;
      standings[awayTeamIndex].points += 1;
    }
  });
  
  // Sort standings by points (primary) and goal difference (secondary)
  return standings.sort((a, b) => {
    if (b.points !== a.points) {
      return b.points - a.points;
    }
    return b.goalDifference - a.goalDifference;
  });
};

/**
 * Get group standings for a specific group
 * @param {Array} teams - Array of team objects
 * @param {Array} matches - Array of match objects
 * @param {string} groupName - Group name (e.g., 'group-1')
 * @returns {Array} Array of team standings objects for the specified group
 */
export const getGroupStandings = (teams, matches, groupName) => {
  // Get matches for this group
  const groupMatches = matches.filter(match => match.round === groupName);
  
  // Get teams in this group (extract from matches)
  const teamIds = new Set();
  groupMatches.forEach(match => {
    teamIds.add(match.homeTeam.id);
    teamIds.add(match.awayTeam.id);
  });
  
  // Filter teams by those in this group
  const groupTeams = teams.filter(team => teamIds.has(team.id));
  
  // Calculate standings for this group
  return calculateStandings(groupTeams, groupMatches);
};

/**
 * Update knockout matches with winners from previous rounds
 * @param {Array} matches - Array of match objects
 */
export const updateKnockoutMatches = (matches) => {
  // Get all knockout matches
  const knockoutMatches = matches.filter(match => 
    match.round.startsWith('knockout-') || 
    match.round.startsWith('final-round-')
  );
  
  // Process each match with a nextMatchId
  knockoutMatches
    .filter(match => match.status === 'completed' && match.nextMatchId)
    .forEach(match => {
      const nextMatch = matches.find(m => m.id === match.nextMatchId);
      if (!nextMatch) return;
      
      // Determine winner
      const winner = match.homeScore > match.awayScore ? match.homeTeam : match.awayTeam;
      
      // Update next match with winner
      if (!nextMatch.homeTeam) {
        nextMatch.homeTeam = winner;
      } else if (!nextMatch.awayTeam) {
        nextMatch.awayTeam = winner;
      }
    });
};

/**
 * Sort rounds in proper numerical order
 * @param {Array} rounds - Array of round names
 * @returns {Array} Sorted array of round names
 */
const sortRounds = (rounds) => {
  return rounds.sort((a, b) => {
    // Extract round type and number
    const [aType, aNum] = a.split('-');
    const [bType, bNum] = b.split('-');
    
    // If types are different, maintain the order (round-robin before finals)
    if (aType !== bType) {
      return aType.localeCompare(bType);
    }
    
    // If types are same, sort by number
    return parseInt(aNum || '0') - parseInt(bNum || '0');
  });
};

/**
 * Generate a tournament bracket structure for visualization
 * @param {Array} matches - Array of match objects
 * @returns {Object} Bracket structure object
 */
export const generateBracketStructure = (matches) => {
  // Get all knockout matches
  const knockoutMatches = matches.filter(match => 
    match.round.startsWith('knockout-') || 
    match.round.startsWith('final-round-')
  );
  
  if (knockoutMatches.length === 0) return null;
  
  // Get all unique rounds and sort them properly
  const rounds = sortRounds([...new Set(knockoutMatches.map(match => match.round))]);
  
  // Create bracket structure
  const bracket = {
    rounds: rounds.map(roundName => {
      const roundMatches = knockoutMatches.filter(match => match.round === roundName);
      
      return {
        name: roundName,
        matches: roundMatches.map(match => ({
          id: match.id,
          homeTeam: match.homeTeam,
          awayTeam: match.awayTeam,
          homeScore: match.homeScore,
          awayScore: match.awayScore,
          status: match.status,
          nextMatchId: match.nextMatchId
        }))
      };
    })
  };
  
  return bracket;
};